import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, message, Button, Dropdown, Space,
  Tooltip, Drawer, Divider, Modal, Form, Select,
  DatePicker, InputNumber, Radio, Alert, Spin
} from "antd";
import {
  SearchOutlined, CrownOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined,
  UserOutlined, MailOutlined, PhoneOutlined, CalendarOutlined,
  IdcardOutlined, TrophyOutlined, StarFilled, FireOutlined,
  CheckCircleOutlined, ClockCircleOutlined, DollarOutlined,
  WhatsAppOutlined, GiftOutlined, SafetyOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getPremiumMembers,
  getPremiumPlans,
  UserGet,
  staffGrantPremiumToUser,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../../Admin_Panel/Utils/exportCSV";
import { exportToPDF } from "../../../Admin_Panel/Utils/exportPDF";
import {
  formatDate, capitalize,
} from "../../../Admin_Panel/Utils/formatters";
import Swal from "sweetalert2";
import "./StaffPremiumMembers.css";

const { Option } = Select;
const { TextArea } = Input;

const PLAN_TYPE_CONFIG = {
  contact_view: { icon: '📱', label: 'Contact View', color: '#3b82f6' },
  specific_user: { icon: '👤', label: 'Specific User', color: '#8b5cf6' },
  profile_boost: { icon: '🚀', label: 'Profile Boost', color: '#f59e0b' },
  custom: { icon: '⚙️', label: 'Custom', color: '#10b981' },
};

const PAYMENT_MODES = [
  { value: 'cash', label: '💵 Cash Received', color: '#10b981' },
  { value: 'online', label: '🏦 Online Payment', color: '#3b82f6' },
  { value: 'upi', label: '📱 UPI Transfer', color: '#8b5cf6' },
  { value: 'bank_transfer', label: '🏛️ Bank Transfer', color: '#6366f1' },
  { value: 'gift', label: '🎁 Free / Complimentary', color: '#ec4899' },
  { value: 'custom', label: '⚙️ Custom / Other', color: '#f59e0b' },
];

function StaffPremiumMembers() {
  const [members, setMembers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [myFreeUsers, setMyFreeUsers] = useState([]);
  const [premiumPlans, setPremiumPlans] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [customAmount, setCustomAmount] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [grantForm] = Form.useForm();

  const staffName = localStorage.getItem("username") || "Staff";
  const staffId = localStorage.getItem("user_id") || "";
  const staffRefer = localStorage.getItem("refer") || "";

  // ============================================
  // ✅ HELPER - Check if user belongs to this staff
  // ============================================
  const belongsToStaff = (user, currentStaffData) => {
    if (!user) return false;

    const staffIdStr = String(staffId || '').trim();
    const staffReferStr = String(
      (currentStaffData && currentStaffData.refer) || staffRefer || ''
    ).trim();

    if (String(user.added_by_staff || '').trim() === staffIdStr && staffIdStr) {
      return true;
    }

    const userReferBy = String(user.refer_by || '').trim();
    if (staffReferStr && userReferBy === staffReferStr) {
      return true;
    }

    if (staffReferStr && userReferBy) {
      const staffReferNum = parseInt(staffReferStr, 10);
      const userReferNum = parseInt(userReferBy, 10);
      if (!isNaN(staffReferNum) && !isNaN(userReferNum) && staffReferNum === userReferNum) {
        return true;
      }
    }

    return false;
  };

  // ============================================
  // FETCH DATA
  // ============================================
  const fetchPremium = async () => {
    try {
      setLoading(true);
      const [premiumData, plansData, allUsersData] = await Promise.all([
        getPremiumMembers(),
        getPremiumPlans(),
        UserGet(),
      ]);

      const allUsersArr = Array.isArray(allUsersData) ? allUsersData : [];
      const currentStaff = allUsersArr.find(u => String(u.id) === String(staffId));

      console.log("🎯 STAFF PREMIUM MEMBERS FETCH");
      console.log("👤 Staff ID:", staffId);
      console.log("👤 Staff Refer:", currentStaff?.refer);
      console.log("📦 Total Premium Members from API:", premiumData?.length);

      const allPremiumMembers = Array.isArray(premiumData) ? premiumData : [];

      // ✅ Filter only staff's users
      const myPremiumMembers = allPremiumMembers.filter(m => {
        const isPremium = m.is_premium === true || m.premium === true;
        if (!isPremium) return false;
        return belongsToStaff(m, currentStaff);
      });

      const myFreeUsersArr = allUsersArr.filter(u => {
        if (u.role !== "user") return false;
        if (u.is_premium || u.premium) return false;
        return belongsToStaff(u, currentStaff);
      });

      setMembers(myPremiumMembers);
      setFiltered(myPremiumMembers);
      setPremiumPlans(Array.isArray(plansData) ? plansData.filter(p => p.status === 'active') : []);
      setMyFreeUsers(myFreeUsersArr);

      console.log("📊 My Premium Members:", myPremiumMembers.length);
      console.log("📊 My Free Users:", myFreeUsersArr.length);
      console.log("📊 Sample Member with all_plans:", myPremiumMembers[0]);
    } catch (error) {
      console.error("❌ Error:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPremium(); }, []);

  useEffect(() => {
    let filtered = [...members];
    if (searchText) {
      filtered = filtered.filter(m =>
        m.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        m.contact?.includes(searchText) ||
        (m.profile_id && m.profile_id.toLowerCase().includes(searchText.toLowerCase()))
      );
    }
    if (planFilter !== "all") {
      filtered = filtered.filter(m => {
        if (m.all_plans && Array.isArray(m.all_plans)) {
          return m.all_plans.some(p =>
            (p.plan_name || "").toLowerCase().includes(planFilter)
          );
        }
        return (m.plan_name || "").toLowerCase().includes(planFilter);
      });
    }
    setFiltered(filtered);
  }, [searchText, planFilter, members]);

  const getPlanConfig = (planName) => {
    const plan = (planName || "premium").toLowerCase();
    if (plan.includes("platinum") || plan.includes("diamond")) {
      return { color: "platinum", icon: <CrownOutlined />, label: capitalize(planName) };
    }
    if (plan.includes("gold")) {
      return { color: "gold", icon: <TrophyOutlined />, label: capitalize(planName) };
    }
    if (plan.includes("silver")) {
      return { color: "silver", icon: <StarFilled />, label: capitalize(planName) };
    }
    return { color: "premium", icon: <CrownOutlined />, label: capitalize(planName) || "Premium" };
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const diffDays = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // ============================================
  // GRANT HANDLER
  // ============================================
  const handleOpenGrantModal = () => {
    grantForm.resetFields();
    setSelectedUser(null);
    setSelectedPlan(null);
    setPaymentMode('cash');
    setCustomAmount(null);
    setStartDate(dayjs());
    setGrantModalOpen(true);
  };

  const handleUserSelect = (userId) => {
    // ✅ Staff can select free OR their own premium users (for extension)
    let user = myFreeUsers.find(u => String(u.id) === String(userId));
    if (!user) {
      user = members.find(m => String(m.id) === String(userId));
    }
    setSelectedUser(user);
  };

  const handlePlanSelect = (planId) => {
    const plan = premiumPlans.find(p => String(p.id) === String(planId));
    setSelectedPlan(plan);
    if (plan) setCustomAmount(plan.price);
  };

  const calculateEndDate = () => {
    if (!selectedPlan || !startDate) return null;
    return dayjs(startDate).add(selectedPlan.duration || 30, 'day');
  };

  // ✅ Check existing plan for extension
  const checkExistingPlan = () => {
    if (!selectedUser || !selectedPlan) return null;
    const existingMember = members.find(m => String(m.id) === String(selectedUser.id));
    if (!existingMember || !existingMember.all_plans) return null;

    return existingMember.all_plans.find(p =>
      String(p.plan_id) === String(selectedPlan.id)
    );
  };

  const existingPlan = checkExistingPlan();

  const handleGrantPremium = async () => {
    try {
      if (!selectedUser) return message.error("Please select a user");
      if (!selectedPlan) return message.error("Please select a plan");

      const values = await grantForm.validateFields();
      const isExtending = !!existingPlan;

      const confirmed = await Swal.fire({
        title: isExtending ? '⭐ Extend Existing Plan?' : '⭐ Grant Premium?',
        html: `
          <div style="text-align:left; padding:10px;">
            <p><strong>User:</strong> ${selectedUser.first_name || selectedUser.username}</p>
            <p><strong>Plan:</strong> ${selectedPlan.plan_name}</p>
            <p><strong>Duration:</strong> ${selectedPlan.duration} days ${isExtending ? '(added to existing)' : ''}</p>
            <p><strong>Amount:</strong> ₹${customAmount || 0}</p>
            <p><strong>Payment:</strong> ${PAYMENT_MODES.find(p => p.value === paymentMode)?.label}</p>
            ${isExtending ? `
              <div style="background:#fef3c7; padding:8px; border-radius:6px; margin-top:10px; font-size:12px; color:#92400e;">
                ⚠️ <strong>Extension Mode</strong><br/>
                Current expiry: ${formatDate(existingPlan.plan_expiry)}<br/>
                New expiry: ${dayjs(existingPlan.plan_expiry).add(selectedPlan.duration, 'day').format('DD MMM YYYY')}
              </div>
            ` : ''}
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff0055',
        confirmButtonText: isExtending ? '✅ Extend Plan' : '✅ Grant Premium',
      });

      if (!confirmed.isConfirmed) return;

      setGrantLoading(true);
      const endDate = calculateEndDate();
      const finalAmount = paymentMode === 'gift' ? 0 : (customAmount || selectedPlan.price);

      try {
        const grantResponse = await staffGrantPremiumToUser({
          user_id: selectedUser.id,
          plan_id: selectedPlan.id,
          amount: finalAmount,
          payment_mode: paymentMode,
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          admin_note: values.admin_note || '',
        });

        console.log("✅ Grant API Success:", grantResponse);
        const wasExtension = grantResponse.is_extension || false;

        Swal.fire({
          title: wasExtension ? '🎉 Plan Extended!' : '🎉 Premium Granted!',
          html: `
            <div style="text-align:center;">
              <p style="font-size:16px; margin:10px 0;">
                ${wasExtension ? 'Plan extended for' : 'Premium granted to'} <strong>${selectedUser.first_name || selectedUser.username}</strong>
              </p>
              <p><strong>Plan:</strong> ${selectedPlan.plan_name}</p>
              <p><strong>${wasExtension ? 'New Expiry' : 'Expires'}:</strong> ${grantResponse.expiry_date ? dayjs(grantResponse.expiry_date).format('DD MMM YYYY') : endDate.format('DD MMM YYYY')}</p>
              <p style="color:#16a34a; margin-top:12px;">
                ✅ Your commission has been added!
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#ff0055',
        });

        setGrantModalOpen(false);
        fetchPremium();

      } catch (grantError) {
        console.error("❌ Grant API failed:", grantError);
        const errorMsg =
          grantError?.response?.data?.error ||
          grantError?.response?.data?.detail ||
          grantError?.response?.data?.message ||
          `Grant failed`;

        Swal.fire({
          title: '❌ Grant Failed',
          text: errorMsg,
          icon: 'error',
          confirmButtonColor: '#dc2626',
        });
      }

    } catch (error) {
      console.error("Grant error:", error);
      message.error(error?.message || "Failed to grant premium");
    } finally {
      setGrantLoading(false);
    }
  };

  const handleView = (record) => {
    setSelectedMember(record);
    setDetailsDrawerOpen(true);
  };

  const handleCall = (contact) => {
    if (!contact) return message.warning("No contact number");
    window.location.href = `tel:${contact}`;
  };

  const handleWhatsApp = (contact) => {
    if (!contact) return message.warning("No contact number");
    window.open(`https://wa.me/${contact.replace(/\D/g, "")}`, "_blank");
  };

  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "profile_id", label: "Profile ID", format: (r) => r.profile_id || "N/A" },
    { key: "name", label: "Name", format: (r) => r.first_name || r.username || "N/A" },
    { key: "contact", label: "Contact", format: (r) => r.contact || "N/A" },
    { key: "total_plans", label: "Total Plans", format: (r) => r.total_plans || 1 },
    { key: "plans", label: "Plans", format: (r) => (r.all_plans || []).map(p => p.plan_name).join(", ") || r.plan_name },
    { key: "total_spent", label: "Total Spent", format: (r) => `₹${parseFloat(r.total_spent || r.plan_amount || 0).toFixed(2)}` },
    { key: "expiry", label: "Expiry", format: (r) => formatDate(r.plan_expiry) },
    { key: "status", label: "Status", format: (r) => isExpired(r.plan_expiry) ? "Expired" : "Active" },
  ];

  const handleExportCSV = () => exportToCSV(members, exportColumns, "my_premium_members");
  const handleExportPDF = () => exportToPDF(members, exportColumns, { title: "My Premium Members", filename: "my_premium_members" });

  const stats = {
    total: members.length,
    active: members.filter(m => !isExpired(m.plan_expiry)).length,
    expired: members.filter(m => isExpired(m.plan_expiry)).length,
    expiringSoon: members.filter(m => isExpiringSoon(m.plan_expiry)).length,
    totalRevenue: members.reduce((sum, m) => sum + parseFloat(m.total_spent || m.plan_amount || 0), 0),
    freeUsers: myFreeUsers.length,
    multiPlanUsers: members.filter(m => (m.total_plans || 1) > 1).length,
  };

  // ============================================
  // COLUMNS
  // ============================================
  const columns = [
    {
      title: "#", key: "sno", width: 45, fixed: "left",
      render: (_, __, i) => <span className="sno-cell">{i + 1}</span>,
    },
    {
      title: "Profile ID", key: "profile_id", width: 120,
      render: (_, r) => <span className="ref-cell">{r.profile_id || 'N/A'}</span>,
    },
    {
      title: "Member", key: "name", width: 200,
      render: (_, r) => {
        const planCfg = getPlanConfig(r.plan_name);
        return (
          <div className="member-cell">
            <div className={`member-avatar ${planCfg.color}`}>
              {(r.first_name?.[0] || r.username?.[0] || "P").toUpperCase()}
              <div className="premium-badge-corner"><CrownOutlined /></div>
            </div>
            <div className="member-info">
              <div className="member-name">
                {r.first_name || r.username || "N/A"}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Mobile", key: "mobile", width: 140,
      render: (_, r) => (
        <div className="mobile-call-cell">
          <span className="mobile-number"><PhoneOutlined /> {r.contact || "N/A"}</span>
        </div>
      ),
    },
    // ✅ Plans column with multiple support
    {
      title: "Plans", key: "plan", width: 140,
      render: (_, r) => {
        const allPlans = r.all_plans || [{ plan_name: r.plan_name, plan_type: r.plan_type }];
        const totalPlans = r.total_plans || 1;

        if (totalPlans === 1) {
          const cfg = getPlanConfig(allPlans[0].plan_name);
          return (
            <Tag className={`plan-tag ${cfg.color}`} style={{ margin: 0 }}>
              {cfg.icon} {cfg.label}
            </Tag>
          );
        }

        return (
          <div style={{
            fontSize: 12,
            color: '#8b5cf6',
            fontWeight: 800,
            background: '#f5f3ff',
            // padding: '6px 12px',
            borderRadius: 8,
            textAlign: 'center',
            border: '1.5px solid #ddd6fe',
          }}>
            {totalPlans} Plans Active
          </div>
        );
      },
    },
    // ✅ Amount with total spent
    {
      title: "Amount", key: "amount", width: 130,
      render: (_, r) => {
        const allPlans = r.all_plans || [{ plan_amount: r.plan_amount }];
        const totalPlans = r.total_plans || 1;

        if (totalPlans === 1) {
          const amt = parseFloat(allPlans[0].plan_amount || 0);
          return (
            <span style={{ fontWeight: 700, fontSize: 14, color: amt === 0 ? '#ec4899' : '#10b981' }}>
              {amt === 0 ? 'FREE' : `₹${amt.toLocaleString("en-IN")}`}
            </span>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            {allPlans.map((plan, idx) => {
              const amt = parseFloat(plan.plan_amount || 0);
              return (
                <span key={idx} style={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: amt === 0 ? '#ec4899' : '#10b981',
                }}>
                  {amt === 0 ? '🎁 FREE' : `₹${amt.toLocaleString("en-IN")}`}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Expiry", key: "expiry", width: 130,
      render: (_, r) => {
        if (!r.plan_expiry) return <span>N/A</span>;
        const days = getDaysRemaining(r.plan_expiry);
        const expired = isExpired(r.plan_expiry);
        return (
          <div className="expiry-cell">
            <div className="expiry-date">{formatDate(r.plan_expiry)}</div>
            {expired ? (
              <span className="expiry-badge expired">Expired</span>
            ) : days <= 7 ? (
              <span className="expiry-badge warning">{days}d left</span>
            ) : (
              <span className="expiry-badge active">{days}d left</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status", key: "status", width: 100,
      render: (_, r) => {
        const expired = isExpired(r.plan_expiry);
        return (
          <Tag className={`status-tag ${expired ? "expired" : "active"}`}>
            {expired ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
            {" "}{expired ? "Expired" : "Active"}
          </Tag>
        );
      },
    },
    {
      title: "Actions", key: "action", width: 130, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} className="action-btn view"
              onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="WhatsApp">
            <Button size="small" icon={<WhatsAppOutlined />} className="action-btn whatsapp"
              onClick={() => handleWhatsApp(record.contact)} />
          </Tooltip>
          <Tooltip title="Call">
            <Button size="small" icon={<PhoneOutlined />} className="action-btn email"
              onClick={() => handleCall(record.contact)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [{ key: "1", label: `Export All (${members.length})`, onClick: handleExportCSV }],
  };

  const pdfMenuItems = {
    items: [{ key: "1", label: `Export All (${members.length})`, onClick: handleExportPDF }],
  };

  return (
    <div className="premium-page">
      <div className="header-stats-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><CrownOutlined /></div>
              <div className="title-content">
                <h2 className="page-title">My Premium Members</h2>
                <p className="page-subtitle">
                  {filtered.length} of {members.length} premium members
                  {stats.multiPlanUsers > 0 && (
                    <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>
                      🎉 {stats.multiPlanUsers} Multi-Plan
                    </Tag>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchPremium} loading={loading} className="icon-btn" />
            </Tooltip>
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn">
                <Space size={4}>CSV<DownOutlined style={{ fontSize: 10 }} /></Space>
              </Button>
            </Dropdown>
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn">
                <Space size={4}>PDF<DownOutlined style={{ fontSize: 10 }} /></Space>
              </Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<GiftOutlined />}
              onClick={handleOpenGrantModal}
              style={{
                background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                border: 'none', fontWeight: 700,
                boxShadow: '0 4px 12px rgba(255,0,85,0.3)',
              }}
            >
              Grant Premium
            </Button>
          </div>
        </div>

        <div style={{
          margin: '0 16px 12px', padding: '10px 14px',
          background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
          border: '1px solid #bfdbfe', borderRadius: 8,
          fontSize: 12, color: '#1e40af',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <SafetyOutlined style={{ fontSize: 14 }} />
          <span>
            <strong>Note:</strong> You can grant/extend premium for your referred users only.
            You have <strong>{myFreeUsers.length}</strong> free users available.
          </span>
        </div>

        <div className="mini-stats-bar">
          <div className="mini-stat total"><CrownOutlined /> <b>{stats.total}</b> <span>Total</span></div>
          <div className="mini-divider" />
          <div className="mini-stat active"><CheckCircleOutlined /> <b>{stats.active}</b> <span>Active</span></div>
          <div className="mini-divider" />
          <div className="mini-stat expiring"><ClockCircleOutlined /> <b>{stats.expiringSoon}</b> <span>Expiring</span></div>
          <div className="mini-divider" />
          <div className="mini-stat expired"><FireOutlined /> <b>{stats.expired}</b> <span>Expired</span></div>
          <div className="mini-divider" />
          <div className="mini-stat" style={{ color: '#a855f7' }}>
            <TrophyOutlined /> <b>{stats.multiPlanUsers}</b> <span>Multi-Plan</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat" style={{ color: '#3b82f6' }}>
            <UserOutlined /> <b>{stats.freeUsers}</b> <span>Free Users</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat revenue">
            <DollarOutlined /> <b>₹{stats.totalRevenue.toLocaleString("en-IN")}</b> <span>Revenue</span>
          </div>
        </div>
      </div>

      <div className="quick-filters-bar">
        <div className="toolbar-right">
          <Input
            placeholder="Search by name, email, mobile, profile ID..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="toolbar-search"
            allowClear
          />
        </div>
      </div>

      {/* ===== TABLE (Desktop Only) ===== */}
      <div className="table-card premium-desktop-only">
        <Table
          rowKey={(record, index) => `member-${record.id}-${index}`}
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            size: "small",
            showTotal: (total) => `Total ${total} unique users`,
          }}
          scroll={{ x: 1150 }}
        />
      </div>

      {/* ===== MOBILE CARDS ===== */}
      <div className="premium-mobile-cards premium-mobile-only">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 12, color: '#999' }}>Loading premium members...</p>
          </div>
        ) : filtered.length > 0 ? (
          filtered.map((member) => {
            const planCfg = getPlanConfig(member.plan_name);
            const expired = isExpired(member.plan_expiry);
            const days = getDaysRemaining(member.plan_expiry);
            const totalPlans = member.total_plans || 1;
            const allPlans = member.all_plans || [{ plan_amount: member.plan_amount, plan_name: member.plan_name }];
            const totalAmount = parseFloat(member.total_spent || member.plan_amount || 0);

            return (
              <div key={member.id} className="pm-mcard">
                {/* Top: Profile ID + Status */}
                <div className="pm-mcard-top">
                  <span className="pm-mcard-pid">{member.profile_id || 'N/A'}</span>
                  <span className={`pm-mcard-status ${expired ? 'expired' : 'active'}`}>
                    {expired ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
                    {expired ? ' Expired' : ' Active'}
                  </span>
                </div>

                {/* Body: Avatar + Info */}
                <div className="pm-mcard-body">
                  <div className={`pm-mcard-avatar ${planCfg.color}`}>
                    {(member.first_name?.[0] || member.username?.[0] || 'P').toUpperCase()}
                    <div className="pm-mcard-crown"><CrownOutlined /></div>
                  </div>

                  <div className="pm-mcard-info">
                    <h3 className="pm-mcard-name">{member.first_name || member.username || 'N/A'}</h3>
                    {member.contact && (
                      <a
                        href={`tel:${member.contact}`}
                        className="pm-mcard-phone"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PhoneOutlined /> {member.contact}
                      </a>
                    )}

                    <div className="pm-mcard-plan-row">
                      {totalPlans === 1 ? (
                        <span className={`pm-mcard-plan-tag ${planCfg.color}`}>
                          {planCfg.icon} {planCfg.label}
                        </span>
                      ) : (
                        <span className="pm-mcard-multi-plan">
                          🎉 {totalPlans} Plans Active
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Amount + Expiry */}
                <div className="pm-mcard-details">
                  <div className="pm-mcard-detail-item">
                    <span className="pm-mcard-detail-label">💰 Amount</span>
                    <span className="pm-mcard-detail-value amount">
                      {totalAmount === 0 ? '🎁 FREE' : `₹${totalAmount.toLocaleString("en-IN")}`}
                    </span>
                  </div>
                  <div className="pm-mcard-detail-item">
                    <span className="pm-mcard-detail-label">📅 Expiry</span>
                    <span className="pm-mcard-detail-value">
                      {member.plan_expiry ? formatDate(member.plan_expiry) : 'N/A'}
                      {!expired && days !== null && (
                        <span className={`pm-mcard-days ${days <= 7 ? 'warning' : ''}`}>
                          {days}d left
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Footer: Actions */}
                <div className="pm-mcard-footer">
                  <button
                    className="pm-mcard-btn view"
                    onClick={() => handleView(member)}
                    title="View Details"
                  >
                    <EyeOutlined /> View
                  </button>
                  <button
                    className="pm-mcard-btn call"
                    onClick={() => handleCall(member.contact)}
                    title="Call"
                  >
                    <PhoneOutlined />
                  </button>
                  <button
                    className="pm-mcard-btn whatsapp"
                    onClick={() => handleWhatsApp(member.contact)}
                    title="WhatsApp"
                  >
                    <WhatsAppOutlined />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state-card">
            <CrownOutlined className="empty-icon" />
            <h3>No Premium Members Found</h3>
            <p>Try adjusting your search or grant premium to users</p>
          </div>
        )}
      </div>

      {/* GRANT MODAL */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 20,
            }}>
              <GiftOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Grant Premium to User</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Grant or extend premium for your users</div>
            </div>
          </div>
        }
        open={grantModalOpen}
        onCancel={() => setGrantModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        centered
      >
        <Form form={grantForm} layout="vertical">
          <div style={{
            display: 'flex', gap: 12, marginBottom: 20,
            padding: 12, background: '#eff6ff', borderRadius: 10,
            border: '1px solid #bfdbfe',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SafetyOutlined style={{ color: '#3b82f6', fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>GRANTED BY (STAFF)</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{staffName}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <UserOutlined style={{ color: '#3b82f6', fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>YOUR USERS</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{myFreeUsers.length + members.length}</div>
              </div>
            </div>
          </div>

          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Select Your User <span style={{ color: '#ef4444' }}>*</span></span>}
            required
          >
            <Select
              placeholder="🔍 Search from YOUR users only..."
              showSearch
              onChange={handleUserSelect}
              filterOption={(input, option) => {
                return String(option.label || '').toLowerCase().includes(input.toLowerCase());
              }}
              options={[
                ...myFreeUsers.map(u => ({
                  value: u.id,
                  label: `${u.first_name || u.username || 'N/A'} - ${u.contact || 'No mobile'} - ${u.profile_id || 'No ID'} (Free)`,
                })),
                ...members.map(u => ({
                  value: u.id,
                  label: `${u.first_name || u.username || 'N/A'} - ${u.contact || 'No mobile'} - ${u.profile_id || 'No ID'} (Premium - ${u.total_plans || 1} plans)`,
                }))
              ]}
              size="large"
              notFoundContent="No users found"
            />
            {selectedUser && (
              <div style={{
                marginTop: 8, padding: 10, background: '#f0f9ff',
                borderRadius: 8, border: '1px solid #bae6fd',
                display: 'flex', gap: 12, alignItems: 'center',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700,
                }}>
                  {selectedUser.first_name?.[0] || selectedUser.username?.[0] || 'U'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{selectedUser.first_name || selectedUser.username}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    📱 {selectedUser.contact} | ID: {selectedUser.profile_id || 'N/A'}
                  </div>
                </div>
                {(selectedUser.is_premium || selectedUser.premium) ? (
                  <Tag color="green">⭐ Premium ({selectedUser.total_plans || 1} plans)</Tag>
                ) : (
                  <Tag color="orange">Free User</Tag>
                )}
              </div>
            )}
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 700 }}>Select Plan <span style={{ color: '#ef4444' }}>*</span></span>}
            required
          >
            <Select
              placeholder="Choose a premium plan..."
              onChange={handlePlanSelect}
              size="large"
              options={premiumPlans.map(p => ({
                value: p.id,
                label: `${PLAN_TYPE_CONFIG[p.plan_type]?.icon || '👑'} ${p.plan_name} - ₹${p.price} (${p.duration} days)`,
              }))}
            />
            {selectedPlan && (
              <div style={{
                marginTop: 8, padding: 12, borderRadius: 10,
                background: PLAN_TYPE_CONFIG[selectedPlan.plan_type]?.color + '15',
                border: `1.5px solid ${PLAN_TYPE_CONFIG[selectedPlan.plan_type]?.color}40`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{selectedPlan.plan_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Duration: {selectedPlan.duration} days
                    </div>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#ff0055' }}>
                    ₹{selectedPlan.price?.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            )}
          </Form.Item>

          {/* ✅ EXTENSION WARNING */}
          {existingPlan && (
            <Alert
              message="⚠️ Extension Mode Detected"
              description={
                <div>
                  <p>User already has "{selectedPlan?.plan_name}" plan.</p>
                  <p style={{ fontSize: 12 }}>
                    <strong>Current Expiry:</strong> {formatDate(existingPlan.plan_expiry)}<br />
                    <strong>Will Extend To:</strong> {dayjs(existingPlan.plan_expiry).add(selectedPlan?.duration || 30, 'day').format('DD MMM YYYY')}
                  </p>
                  <p style={{ fontSize: 12, color: '#059669' }}>
                    ✅ Duration + Amount + Limits will be added
                  </p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item label={<span style={{ fontWeight: 700 }}>Payment Mode</span>}>
            <Radio.Group value={paymentMode} onChange={e => setPaymentMode(e.target.value)}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {PAYMENT_MODES.map(mode => (
                <Radio.Button key={mode.value} value={mode.value}
                  style={{ textAlign: 'center', borderRadius: 8, fontSize: 12 }}>
                  {mode.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item label={<span style={{ fontWeight: 700 }}>Amount (₹)</span>}>
              <InputNumber
                value={paymentMode === 'gift' ? 0 : customAmount}
                onChange={setCustomAmount}
                min={0}
                disabled={paymentMode === 'gift'}
                style={{ width: '100%' }}
                prefix="₹"
                size="large"
              />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 700 }}>Start Date</span>}>
              <DatePicker value={startDate} onChange={setStartDate} style={{ width: '100%' }} size="large" allowClear={false} />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 700 }}>End Date (Auto)</span>}>
              <Input
                value={calculateEndDate() ? calculateEndDate().format('DD MMM YYYY') : 'Select plan first'}
                readOnly size="large"
                style={{ background: '#f8fafc', fontWeight: 600 }}
              />
            </Form.Item>
          </div>

          <Form.Item label={<span style={{ fontWeight: 700 }}>Note (Optional)</span>} name="admin_note">
            <TextArea rows={2} placeholder="Add a note..." maxLength={200} showCount />
          </Form.Item>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button size="large" onClick={() => setGrantModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              size="large"
              loading={grantLoading}
              onClick={handleGrantPremium}
              icon={<CrownOutlined />}
              style={{
                background: existingPlan
                  ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
                  : 'linear-gradient(135deg, #ff0055, #ff4d88)',
                border: 'none', fontWeight: 700, padding: '0 32px',
              }}
            >
              {existingPlan ? '⭐ Extend Plan Now' : '⭐ Grant Premium Now'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* DETAILS DRAWER - Multi-Plan Support */}
      <Drawer
        title={<div className="drawer-title"><CrownOutlined /> Premium Member Details</div>}
        placement="right"
        width={480}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
      >
        {selectedMember && (
          <div className="member-details">
            <div className="member-detail-header">
              <div className={`detail-avatar ${getPlanConfig(selectedMember.plan_name).color}`}>
                {(selectedMember.first_name?.[0] || selectedMember.username?.[0] || "P").toUpperCase()}
                <div className="premium-badge-corner-lg"><CrownOutlined /></div>
              </div>
              <h2>{selectedMember.first_name || selectedMember.username || "N/A"}</h2>
              <p className="detail-ref">#{selectedMember.profile_id || 'N/A'}</p>
              <div className="detail-tags">
                <Tag className={`plan-tag ${getPlanConfig(selectedMember.plan_name).color}`}>
                  {getPlanConfig(selectedMember.plan_name).icon} {getPlanConfig(selectedMember.plan_name).label}
                </Tag>
                {(selectedMember.total_plans || 1) > 1 && (
                  <Tag color="magenta">🎉 Multi-Plan ({selectedMember.total_plans})</Tag>
                )}
              </div>
            </div>

            <Divider />

            <div className="detail-section">
              <h4>
                <CrownOutlined /> Plans ({selectedMember.total_plans || 1})
              </h4>

              {(() => {
                const allPlans = selectedMember.all_plans || [selectedMember];

                const planGroups = {};
                allPlans.forEach(plan => {
                  const key = plan.plan_name || 'Premium';
                  if (!planGroups[key]) {
                    planGroups[key] = {
                      ...plan,
                      count: 1,
                      total_amount: parseFloat(plan.plan_amount || 0),
                      purchases: [plan],
                    };
                  } else {
                    planGroups[key].count += 1;
                    planGroups[key].total_amount += parseFloat(plan.plan_amount || 0);
                    planGroups[key].purchases.push(plan);
                    if (plan.plan_expiry > planGroups[key].plan_expiry) {
                      planGroups[key].plan_expiry = plan.plan_expiry;
                    }
                  }
                });

                return Object.values(planGroups).map((group, idx) => {
                  const cfg = getPlanConfig(group.plan_name);
                  const typeCfg = PLAN_TYPE_CONFIG[group.plan_type] || {};
                  const expired = isExpired(group.plan_expiry);

                  return (
                    <div key={idx} style={{
                      background: '#fff',
                      border: `2px solid ${expired ? '#fecaca' : '#e2e8f0'}`,
                      borderRadius: 12, padding: 12, marginBottom: 10,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Tag className={`plan-tag ${cfg.color}`} style={{ fontSize: 12, margin: 0 }}>
                            {cfg.icon} {group.plan_name}
                          </Tag>
                          {group.count > 1 && (
                            <Tag color="purple" style={{ fontWeight: 800, margin: 0 }}>
                              x{group.count}
                            </Tag>
                          )}
                        </div>
                        {group.plan_type && typeCfg.icon && (
                          <span style={{ fontSize: 10, color: typeCfg.color, fontWeight: 700 }}>
                            {typeCfg.icon} {typeCfg.label}
                          </span>
                        )}
                      </div>

                      {group.purchases.map((purchase, pIdx) => (
                        <div key={pIdx} style={{
                          background: pIdx % 2 === 0 ? '#f8fafc' : '#fff',
                          border: '1px solid #f1f5f9',
                          borderRadius: 8, padding: 10, marginBottom: 6,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                              Purchase #{pIdx + 1}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: parseFloat(purchase.plan_amount || 0) === 0 ? '#ec4899' : '#ff0055' }}>
                              {parseFloat(purchase.plan_amount || 0) === 0 ? '🎁 FREE' : `₹${parseFloat(purchase.plan_amount || 0).toLocaleString("en-IN")}`}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11 }}>
                            <div>
                              <div style={{ color: '#94a3b8', fontSize: 10 }}>Started</div>
                              <div style={{ fontWeight: 600 }}>{formatDate(purchase.plan_start_date)}</div>
                            </div>
                            <div>
                              <div style={{ color: '#94a3b8', fontSize: 10 }}>Expires</div>
                              <div style={{ color: isExpired(purchase.plan_expiry) ? '#dc2626' : '#10b981', fontWeight: 600 }}>
                                {formatDate(purchase.plan_expiry)}
                              </div>
                            </div>
                          </div>

                          {purchase.payment_mode && (
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                              {purchase.payment_mode === 'gift' ? '🎁 Free' : `💳 via ${purchase.payment_mode}`}
                            </div>
                          )}

                          {purchase.granted_by_name && (
                            <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 4 }}>
                              👑 by {purchase.granted_by_name}
                            </div>
                          )}
                        </div>
                      ))}

                      {(group.contact_view_limit > 0 || group.profile_view_limit > 0) && (
                        <div style={{
                          padding: 8, background: '#f0f9ff',
                          borderRadius: 6, fontSize: 11, color: '#0369a1', marginTop: 4,
                        }}>
                          {group.contact_view_limit > 0 && (
                            <div>📱 Total Contact Views: <strong>{group.contact_view_limit}</strong></div>
                          )}
                          {group.profile_view_limit > 0 && (
                            <div>👤 Total Profile Views: <strong>{group.profile_view_limit}</strong></div>
                          )}
                        </div>
                      )}

                      {group.count > 1 && (
                        <div style={{
                          marginTop: 8, padding: 8,
                          background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                          borderRadius: 6, fontSize: 11,
                          display: 'flex', justifyContent: 'space-between',
                        }}>
                          <span style={{ color: '#7c3aed', fontWeight: 700 }}>
                            📊 {group.count} times purchased
                          </span>
                          <span style={{ color: '#7c3aed', fontWeight: 800 }}>
                            Total: ₹{group.total_amount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      )}

                      {group.admin_note && (
                        <div style={{
                          marginTop: 6, padding: 6,
                          background: '#fef3f7', borderRadius: 6,
                          fontSize: 10, fontStyle: 'italic',
                        }}>
                          📝 {group.admin_note}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}

              {(selectedMember.total_plans || 1) > 1 && (
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                  border: '1.5px solid #86efac',
                  borderRadius: 10, padding: 12, marginTop: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: '#065f46', fontWeight: 700 }}>💰 GRAND TOTAL</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: '#047857' }}>
                      ₹{parseFloat(selectedMember.total_spent || 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#059669', marginTop: 4 }}>
                    Across {selectedMember.total_plans} purchase(s)
                  </div>
                </div>
              )}
            </div>

            <Divider />

            <div className="detail-section">
              <h4><UserOutlined /> Contact Info</h4>
              <div className="detail-item"><MailOutlined /><strong>Email</strong><span>{selectedMember.email || "N/A"}</span></div>
              <div className="detail-item"><PhoneOutlined /><strong>Mobile</strong><span>{selectedMember.contact || "N/A"}</span></div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default StaffPremiumMembers;